import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';
import { getCategory } from '@/mocks/categories';
import { formatViews } from '@/utils/format';

interface ComicCardProps {
  comic: Comic;
  showDescription?: boolean;
}

export default function ComicCard({ comic, showDescription = false }: ComicCardProps) {
  const category = getCategory(comic.category);

  return (
    <article className="group h-full">
      <Link to={`/comic/${comic.id}`} className="block h-full" aria-label={`阅读《${comic.title}》漫画`}>
        <div className="relative overflow-hidden rounded-lg bg-background-100 hover-lift">
          <div className="relative aspect-[2/3] w-full overflow-hidden">
            <img
              src={comic.cover}
              alt={`${comic.title}漫画封面`}
              title={`${comic.title}漫画-免费阅读`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>

          <div className="absolute left-2 top-2 flex items-center gap-1.5">
            <span className="rounded-md bg-background-50/90 px-2 py-0.5 text-xs text-primary-700 font-medium backdrop-blur-sm">
              {category.name}
            </span>
          </div>
          <div className="absolute right-2 top-2">
            <span className="rounded-md bg-accent-500/90 px-2 py-0.5 text-xs text-foreground-950 font-medium backdrop-blur-sm">
              {comic.status}
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-2.5 pb-2 translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
            <span className="inline-flex items-center gap-1 text-xs text-background-50">
              <i className="ri-eye-line text-sm" aria-hidden="true" />
              {formatViews(comic.views)}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-accent-300">
              <i className="ri-star-fill text-sm" aria-hidden="true" />
              {comic.rating.toFixed(1)}
            </span>
          </div>
        </div>

        <h3 className="mt-2.5 text-sm md:text-base font-medium text-foreground-950 group-hover:text-primary-600 transition-colors whitespace-nowrap overflow-hidden text-ellipsis">
          {comic.title}
        </h3>
        <p className="mt-1 flex items-center gap-2 text-xs text-foreground-600">
          <span className="whitespace-nowrap">{comic.author}</span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line text-sm" aria-hidden="true" />
            {formatViews(comic.views)}
          </span>
        </p>
        {showDescription && (
          <p className="mt-2 text-xs leading-5 text-foreground-700 line-clamp-2">{comic.description}</p>
        )}
      </Link>
    </article>
  );
}